'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  Heart,
  MapPin,
  Crown,
  Tag,
  Sparkles,
  Star,
  Trash2,
  ChevronLeft,
  Lock,
} from 'lucide-react';
import { Offer } from '@/types';
import SafeImage from '@/components/SafeImage';
import BottomNav from '@/components/BottomNav';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

export default function FavoritesPageContent() {
  const { user, favoriteIds, toggleFavorite } = useAuth();
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const loadFavoriteOffers = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('favorites')
          .select(`
            offer_id,
            offers (
              id, title, description, city, address, latitude, longitude,
              price, discount_text, discount_amount, promo_code, image_url,
              website_url, requires_plus, promo_single_use, is_special,
              is_epic_location, epic_directions, booking_type, offer_type,
              opening_hours, recurring_days, on_request_offers, status,
              starts_at, ends_at, created_at, updated_at,
              offer_categories ( categories ( id, name, slug, icon, active, sort_order, created_at ) )
            )
          `)
          .eq('user_id', user.id);

        if (error) throw error;

        const mapped: Offer[] = (data ?? [])
          .map((row: any) => {
            const o = row.offers;
            if (!o) return null;
            return {
              ...o,
              categories: (o.offer_categories ?? []).map((oc: any) => oc.categories).filter(Boolean),
              is_favorited: true,
            };
          })
          .filter(Boolean) as Offer[];

        setOffers(mapped);
      } catch (err) {
        toast.error('Fehler beim Laden der Favoriten');
      } finally {
        setLoading(false);
      }
    };
    loadFavoriteOffers();
  }, [user, favoriteIds]);

  const handleRemove = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await toggleFavorite(id);
    setOffers((prev) => prev.filter((o) => o.id !== id));
    toast.success('Aus Favoriten entfernt', { icon: '💔', duration: 1800 });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 pb-24" style={{ background: 'var(--background)' }}>
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <Lock size={28} color="var(--muted-foreground)" />
        </div>
        <h2 className="text-base font-bold mb-2" style={{ color: 'var(--card-foreground)' }}>
          Anmeldung erforderlich
        </h2>
        <p className="text-sm text-center mb-5" style={{ color: 'var(--muted-foreground)' }}>
          Melde dich an, um deine gespeicherten Favoriten zu sehen.
        </p>
        <Link
          href="/sign-up-login-screen"
          className="px-6 py-2.5 rounded-pill text-sm font-semibold text-white transition-all duration-150 active:scale-95"
          style={{ background: 'var(--primary)' }}
        >
          Jetzt anmelden
        </Link>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ background: 'var(--background)' }}>
      {/* ── HEADER ── */}
      <div
        className="sticky top-0 z-40 px-4"
        style={{
          background: 'rgba(27,29,35,0.96)',
          borderBottom: '1px solid var(--border)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: '12px',
        }}
      >
        <div className="flex items-center justify-between max-w-mobile mx-auto">
          <Link
            href="/"
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
            style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}
            aria-label="Zurück zur Entdecken-Seite"
          >
            <ChevronLeft size={18} color="var(--card-foreground)" />
          </Link>

          <Image
            src="/assets/images/logo_grun_fur_app-1787922846254.png"
            alt="WINEWOOD Adventure"
            width={110}
            height={44}
            className="object-contain"
            priority
          />

          <div className="w-9" />
        </div>
      </div>

      {/* ── PAGE TITLE ── */}
      <div className="px-4 pt-5 pb-4 max-w-mobile mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Heart size={18} color="#ef4444" fill="#ef4444" />
          <h1 className="text-lg font-bold" style={{ color: 'var(--card-foreground)' }}>
            Meine Favoriten
          </h1>
        </div>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          {loading
            ? 'Wird geladen…'
            : offers.length === 0
            ? 'Noch keine gespeicherten Angebote'
            : `${offers.length} gespeicherte Angebote`}
        </p>
      </div>

      {/* ── FAVORITES LIST ── */}
      <div className="px-4 max-w-mobile mx-auto">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--primary)', borderTopColor: 'transparent' }} />
          </div>
        ) : offers.length === 0 ? (
          <div className="text-center py-16">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: 'var(--muted)' }}
            >
              <Heart size={28} color="var(--muted-foreground)" />
            </div>
            <p className="text-sm font-semibold mb-1" style={{ color: 'var(--card-foreground)' }}>
              Noch keine Favoriten
            </p>
            <p className="text-xs mb-5" style={{ color: 'var(--muted-foreground)' }}>
              Tippe auf das Herz-Symbol bei einem Angebot, um es hier zu speichern.
            </p>
            <Link
              href="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-pill text-sm font-semibold text-white transition-all duration-150 active:scale-95"
              style={{ background: 'var(--primary)' }}
            >
              Angebote entdecken
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {offers.map((offer) => {
              const isFree = offer.price === 0;
              const hasDiscount = offer.discount_amount > 0;
              const discountedPrice = offer.price - offer.discount_amount;
              const isEpic = offer.is_epic_location;
              const isSpecial = offer.is_special;

              const cardBorderColor = isEpic
                ? 'rgba(168,85,247,0.5)'
                : isSpecial
                ? 'rgba(124,179,66,0.45)'
                : 'var(--border)';

              const cardGlow = isEpic
                ? '0 0 18px rgba(168,85,247,0.22), 0 4px 16px rgba(0,0,0,0.4)'
                : isSpecial
                ? '0 0 18px rgba(124,179,66,0.18), 0 4px 16px rgba(0,0,0,0.4)'
                : '0 4px 16px rgba(0,0,0,0.4)';

              return (
                <Link key={`fav-${offer.id}`} href={`/offer-detail-page?id=${offer.id}`}>
                  <div
                    className="rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.98]"
                    style={{
                      background: 'var(--card)',
                      border: `1.5px solid ${cardBorderColor}`,
                      boxShadow: cardGlow,
                    }}
                  >
                    {/* Image */}
                    <div className="relative" style={{ height: '190px' }}>
                      <SafeImage
                        src={offer.image_url}
                        alt={`${offer.title} in ${offer.city}`}
                        fill
                        className="object-cover"
                        sizes="(max-width: 430px) 100vw, 430px"
                      />
                      <div
                        className="absolute inset-0"
                        style={{
                          background:
                            'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0) 100%)',
                        }}
                      />

                      {/* Remove button */}
                      <button
                        onClick={(e) => handleRemove(offer.id, e)}
                        className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                        style={{ background: 'rgba(239,68,68,0.75)', backdropFilter: 'blur(8px)' }}
                        aria-label="Aus Favoriten entfernen"
                      >
                        <Trash2 size={15} color="white" />
                      </button>

                      {/* Distance badge */}
                      {offer.distance_km !== undefined && (
                        <div
                          className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-pill"
                          style={{ background: 'rgba(0,0,0,0.58)', backdropFilter: 'blur(8px)' }}
                        >
                          <MapPin size={11} color={isEpic ? '#a855f7' : 'var(--primary)'} />
                          <span className="text-[11px] font-semibold text-white" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {offer.distance_km < 1
                              ? `${(offer.distance_km * 1000).toFixed(0)} m`
                              : `${offer.distance_km.toFixed(1)} km`}
                          </span>
                        </div>
                      )}

                      {/* Epic / Special badge */}
                      <div className="absolute bottom-3 left-3 flex gap-1.5">
                        {isEpic && (
                          <div className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white" style={{ background: '#a855f7' }}>
                            <Sparkles size={11} />
                            Epic
                          </div>
                        )}
                        {!isEpic && isSpecial && (
                          <div className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white" style={{ background: 'var(--primary)' }}>
                            <Star size={11} fill="white" />
                            Spezial
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="p-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold leading-tight" style={{ color: 'var(--card-foreground)' }}>
                            {offer.title}
                          </h3>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin size={11} color="var(--muted-foreground)" />
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{offer.city}</span>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right flex-shrink-0">
                          {isFree ? (
                            <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>Kostenlos</span>
                          ) : hasDiscount && offer.requires_plus ? (
                            <div className="flex flex-col items-end">
                              <span className="text-xs line-through" style={{ color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                                {offer.price.toFixed(2).replace('.', ',')} €
                              </span>
                              <div className="flex items-center gap-1">
                                <Crown size={11} color="var(--gold)" />
                                <span className="text-sm font-bold" style={{ color: 'var(--gold)', fontVariantNumeric: 'tabular-nums' }}>
                                  {discountedPrice.toFixed(2).replace('.', ',')} €
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm font-bold" style={{ color: 'var(--card-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                              {offer.price.toFixed(2).replace('.', ',')} €
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Discount hint */}
                      {offer.discount_text && (
                        <div className="mt-2.5 flex items-center gap-1.5">
                          <div
                            className="flex items-center gap-1 px-2.5 py-1 rounded-pill"
                            style={{
                              background: offer.requires_plus ? 'rgba(234,179,8,0.1)' : 'rgba(124,179,66,0.12)',
                              border: `1px solid ${offer.requires_plus ? 'rgba(234,179,8,0.3)' : 'rgba(124,179,66,0.35)'}`,
                            }}
                          >
                            {offer.requires_plus ? <Crown size={11} color="var(--gold)" /> : <Tag size={11} color="var(--primary)" />}
                            <span className="text-[11px] font-semibold" style={{ color: offer.requires_plus ? 'var(--gold)' : 'var(--primary)' }}>
                              {offer.discount_text}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Category badges */}
                      {offer.categories.length > 0 && (
                        <div className="flex gap-1.5 mt-2.5 flex-wrap">
                          {offer.categories.slice(0, 3).map((cat) => (
                            <span
                              key={`favcat-${offer.id}-${cat.id}`}
                              className="text-[10px] font-medium px-2 py-0.5 rounded-pill"
                              style={{
                                background: isEpic ? 'rgba(168,85,247,0.12)' : 'var(--muted)',
                                color: isEpic ? '#a855f7' : 'var(--muted-foreground)',
                                border: isEpic ? '1px solid rgba(168,85,247,0.25)' : 'none',
                              }}
                            >
                              {cat.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
